import {
  AUTH_PUBLIC_ID_KEY,
  AUTH_REFRESH_TOKEN_KEY,
  AUTH_ROLE_KEY,
  AUTH_STATUS_KEY,
  AUTH_STUDENT_PUBLIC_ID_KEY,
  AUTH_TOKEN_KEY,
  AUTH_USER_NAME_KEY,
} from '../constants/auth-storage.constants';

export interface TokenPair {
  token: string;
  refreshToken: string | null;
}

export interface StoredSession {
  token: string | null;
  refreshToken: string | null;
  userName: string | null;
  role: string | null;
  publicId: string | null;
  status: string | null;
}

// Single owner of the session keys written by the auth feature: token pair, identity and status.
// The token and the refresh token are only ever written together so they cannot drift apart.
// Two keys are still written elsewhere on purpose: the cross-tab logout event by the logout and
// session-expired flows, and `auth_studentPublicId` by `student-portal.service.ts`. Clearing the
// session goes through here for all of them except the logout event.

export function readStoredSession(): StoredSession {
  return {
    token: localStorage.getItem(AUTH_TOKEN_KEY),
    refreshToken: localStorage.getItem(AUTH_REFRESH_TOKEN_KEY),
    userName: localStorage.getItem(AUTH_USER_NAME_KEY),
    role: localStorage.getItem(AUTH_ROLE_KEY),
    publicId: localStorage.getItem(AUTH_PUBLIC_ID_KEY),
    status: localStorage.getItem(AUTH_STATUS_KEY),
  };
}

export function readStoredToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function readStoredStatus(): string | null {
  return localStorage.getItem(AUTH_STATUS_KEY);
}

export function writeStoredSession(
  session: TokenPair & {
    userName: string;
    role: string;
    publicId: string;
    status: string | null;
  },
): void {
  writeTokenPair(session);
  localStorage.setItem(AUTH_USER_NAME_KEY, session.userName);
  localStorage.setItem(AUTH_ROLE_KEY, session.role);
  localStorage.setItem(AUTH_PUBLIC_ID_KEY, session.publicId);
  setOrRemove(AUTH_STATUS_KEY, session.status);
}

// Used by login and by every renewal: the new pair replaces the old one in one step.
export function writeTokenPair({ token, refreshToken }: TokenPair): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  setOrRemove(AUTH_REFRESH_TOKEN_KEY, refreshToken);
}

export function writeStoredStatus(status: string): void {
  localStorage.setItem(AUTH_STATUS_KEY, status);
}

export function clearStoredSession(): void {
  [
    AUTH_TOKEN_KEY,
    AUTH_REFRESH_TOKEN_KEY,
    AUTH_USER_NAME_KEY,
    AUTH_ROLE_KEY,
    AUTH_PUBLIC_ID_KEY,
    AUTH_STUDENT_PUBLIC_ID_KEY,
    AUTH_STATUS_KEY,
  ].forEach((key) => localStorage.removeItem(key));
}

function setOrRemove(key: string, value: string | null): void {
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }
}
